import React, { useState, useRef, useEffect } from 'react';
import { Download, FileSpreadsheet, FileText, ChevronDown } from 'lucide-react';

const RawFeedbackDownloadDropdown = ({
  onDownloadPdf,
  onDownloadExcel,
  isDownloadingPdf = false,
  isDownloadingExcel = false,
  universityName = 'All',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const isBusy = isDownloadingPdf || isDownloadingExcel;

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelectPdf = () => {
    setIsOpen(false);
    if (onDownloadPdf) onDownloadPdf();
  };

  const handleSelectExcel = () => {
    setIsOpen(false);
    if (onDownloadExcel) onDownloadExcel();
  };

  const getButtonLabel = () => {
    if (isDownloadingPdf) return 'Generating PDF...';
    if (isDownloadingExcel) return 'Generating Excel...';
    return 'Download Raw Data Report';
  };

  return (
    <div 
      ref={dropdownRef} 
      style={{ 
        position: 'relative', 
        marginLeft: 'auto',
        display: 'inline-block' 
      }}
    >
      <button
        type="button"
        onClick={() => !isBusy && !disabled && setIsOpen(prev => !prev)}
        disabled={isBusy || disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: '#10b981',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: (isBusy || disabled) ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease',
          opacity: (isBusy || disabled) ? 0.7 : 1,
          boxShadow: '0 2px 6px rgba(16, 185, 129, 0.25)'
        }}
        onMouseOver={(e) => {
          if (!isBusy && !disabled) e.currentTarget.style.opacity = '0.92';
        }}
        onMouseOut={(e) => {
          if (!isBusy && !disabled) e.currentTarget.style.opacity = '1';
        }}
        title={universityName !== 'All' ? `Download raw report for ${universityName}` : 'Download raw report for all students'}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <Download size={16} />
        <span>{getButtonLabel()}</span>
        <ChevronDown 
          size={14} 
          style={{ 
            transition: 'transform 0.2s ease', 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            marginLeft: '2px'
          }} 
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: '240px',
            background: 'var(--bg-card, #1e242e)',
            border: '1px solid var(--border, rgba(245, 247, 250, 0.14))',
            borderRadius: '10px',
            padding: '6px',
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35), 0 4px 10px rgba(0, 0, 0, 0.2)',
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
            animation: 'fadeIn 0.15s ease-out'
          }}
          role="menu"
        >
          {/* Option: Excel */}
          <button
            type="button"
            onClick={handleSelectExcel}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderRadius: '7px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.18s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-card-hover, rgba(245, 247, 250, 0.08))';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            role="menuitem"
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '7px',
                background: 'rgba(16, 185, 129, 0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <FileSpreadsheet size={17} color="#10b981" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.86rem', color: 'var(--text-main, #ffffff)' }}>
                Download as Excel
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-muted, #94a3b8)', marginTop: '2px' }}>
                Raw responses spreadsheet (.xlsx)
              </div>
            </div>
          </button>

          <div 
            style={{ 
              height: '1px', 
              background: 'var(--border, rgba(245, 247, 250, 0.10))', 
              margin: '4px 6px' 
            }} 
          />

          {/* Option: PDF */}
          <button
            type="button"
            onClick={handleSelectPdf}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '10px 12px',
              background: 'transparent',
              border: 'none',
              borderRadius: '7px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'background 0.18s ease'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-card-hover, rgba(245, 247, 250, 0.08))';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
            role="menuitem"
          >
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '7px',
                background: 'rgba(37, 99, 235, 0.14)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <FileText size={17} color="#3b82f6" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '0.86rem', color: 'var(--text-main, #ffffff)' }}>
                Download as PDF
              </div>
              <div style={{ fontSize: '0.74rem', color: 'var(--text-muted, #94a3b8)', marginTop: '2px' }}>
                Formatted evaluation report (.pdf)
              </div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
};

export default RawFeedbackDownloadDropdown;
